import { useCallback, useMemo, useRef, useState } from 'react';

import { ICellRendererParams, SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useIsMobile } from '@/app/hooks/useIsMobile';
import {
  FilterBar,
  GridLoading,
  ResponsiveDataView,
  StatusBadge,
  statusCellStyle,
  workflowStatusColors,
} from '@/components/common';
import TechniciansDropdown from '@/components/common/technicians-dropdown';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { getDateString } from '@/pages/fert-work/utils/dateHelpers';
import { statusCodeToLabel } from '@/pages/fert-work/utils/statusHelpers';
import { TechnicianItem } from '@/services/lookups/types';
import { useSites } from '@/services/sites';
import { useContextMenu } from '@/utils/context-menu';

export type SessionHistoryGridProps = {
  sessions: any[];
  isLoading?: boolean;
  hasWritePermission: boolean;
  onDelete: (sessionId: string) => void;
  onDeleteMultiple?: (sessionIds: string[]) => Promise<void>;
  onRowDoubleClick?: (sessionId: string, date: string, siteId: string) => void;
  onFilterChange?: (filters: {
    siteId?: string | null;
    date?: string | null;
    status?: string | null;
    createdById?: string | null;
  }) => void;
};

type SessionHistoryRow = {
  id: string;
  date: string;
  aspirationDate: string;
  siteName: string;
  siteId: string;
  status: number;
  createdBy: string;
  runCount: number;
  sessionId: string;
  sessionType: number;
};

const statusOptions = [
  { label: 'Validating', value: 0 },
  { label: 'In Progress', value: 1 },
  { label: 'Issues', value: 4 },
  { label: 'Complete', value: 2 },
  { label: 'Error', value: 3 },
];

const getActionLabel = (status: number) => {
  switch (status) {
    case 0:
      return 'View';
    case 1:
      return 'Continue';
    case 4:
      return 'Fix Issues';
    case 2:
      return 'View';
    case 3:
      return 'Review';
    default:
      return 'Open';
  }
};

const ActionsRenderer = (props: ICellRendererParams) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { onDelete, hasWritePermission } = props.context;

  const handleOpenSession = () => {
    navigate(
      `/fert-work/session?sessionId=${props.data.id}&date=${props.data.date}&siteId=${props.data.siteId}`,
    );
  };

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-blue-600 transition-colors hover:text-blue-800 hover:underline"
        onClick={handleOpenSession}
      >
        {getActionLabel(props.data.status)}
      </button>
      {hasWritePermission && (
        <>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-red-600 transition-colors hover:text-red-800 hover:underline"
            onClick={() => onDelete(props.data.id)}
          >
            {t('common.delete', 'Delete')}
          </button>
        </>
      )}
    </div>
  );
};

const SessionHistoryCard = ({
  session,
  hasWritePermission,
  onOpen,
  onDelete,
}: {
  session: SessionHistoryRow;
  hasWritePermission: boolean;
  onOpen: (session: SessionHistoryRow) => void;
  onDelete: (sessionId: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-start justify-between">
        <div>
          <div className="font-semibold text-gray-900">{session.siteName}</div>
          <div className="text-sm text-gray-500">{session.date}</div>
        </div>
        <StatusBadge
          label={statusCodeToLabel(session.status)}
          colorMap={workflowStatusColors}
        />
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-gray-500">
            {t('fertWork.aspirationLabel', 'Aspiration:')}
          </span>{' '}
          {session.aspirationDate || '-'}
        </div>
        <div>
          <span className="text-gray-500">
            {t('fertWork.runsCardLabel', 'Runs:')}
          </span>{' '}
          {session.runCount}
        </div>
        <div>
          <span className="text-gray-500">
            {t('fertWork.createdByCardLabel', 'Created By:')}
          </span>{' '}
          {session.createdBy || '-'}
        </div>
        <div>
          <span className="text-gray-500">
            {t('fertWork.idCardLabel', 'ID:')}
          </span>{' '}
          {session.sessionId?.slice(0, 8).toUpperCase() || '-'}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-2">
        <button
          type="button"
          className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-blue-600 transition-colors hover:text-blue-800 hover:underline"
          onClick={() => onOpen(session)}
        >
          {getActionLabel(session.status)}
        </button>
        {hasWritePermission && (
          <>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              className="m-0 cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-red-600 transition-colors hover:text-red-800 hover:underline"
              onClick={() => onDelete(session.id)}
            >
              {t('common.delete', 'Delete')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const SessionHistoryGrid = ({
  sessions,
  isLoading,
  hasWritePermission,
  onDelete,
  onDeleteMultiple,
  onRowDoubleClick,
  onFilterChange,
}: SessionHistoryGridProps) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact>(null);
  const { getContextMenuItems } = useContextMenu({ idKey: 'id' });
  const isMobile = useIsMobile();
  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        maxWidth: 50,
        suppressMenu: true,
        sortable: false,
        filter: false,
        resizable: false,
        suppressMovable: true,
        lockPosition: true,
      },
      {
        field: 'date',
        headerName: t('fertWork.sessionDate', 'Session Date'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'status',
        headerName: t('fertWork.status', 'Status'),
        sortable: true,
        filter: true,
        flex: 1,
        valueFormatter: (params: any) => statusCodeToLabel(params.value),
        cellStyle: statusCellStyle(workflowStatusColors, statusCodeToLabel),
      },
      {
        field: 'siteName',
        headerName: t('fertWork.site', 'Site'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'sessionType',
        headerName: t('fertWork.sessionType', 'Session Type'),
        sortable: true,
        filter: true,
        flex: 1,
        valueFormatter: (params: any) =>
          params.value === 1 ? 'Commercial' : 'Conv / SF',
      },
      {
        field: 'createdBy',
        headerName: t('fertWork.createdBy', 'Created By'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'runCount',
        headerName: t('fertWork.runCount', 'Run Count'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'sessionId',
        headerName: t('fertWork.sessionId', 'Session ID'),
        sortable: true,
        filter: true,
        flex: 1,
        valueFormatter: (params: any) => {
          if (!params.value) return '';
          const guid = params.value.toString();
          return guid.slice(0, 8).toUpperCase();
        },
        tooltipField: 'sessionId',
      },
      {
        field: 'aspirationDate',
        headerName: t('fertWork.aspirationDateHeader', 'Aspiration Date'),
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: 'actions',
        headerName: t('fertWork.actions', 'Actions'),
        cellRenderer: ActionsRenderer,
        sortable: false,
        filter: false,
        flex: 1,
      },
    ],
    [t],
  );

  const [filterDate, setFilterDate] = useState<Date | null>(null);
  const [filterSiteId, setFilterSiteId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<number | null>(null);
  const [filterTech, setFilterTech] = useState<TechnicianItem | null>(null);
  const [selectedRows, setSelectedRows] = useState<SessionHistoryRow[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: fertilizationSites } = useSites({ filter: 'fertilization' });

  const handleSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    const selected = event.api.getSelectedRows() as SessionHistoryRow[];
    setSelectedRows(selected);
  }, []);

  const handleDeleteSelected = useCallback(() => {
    if (selectedRows.length === 0) return;

    confirm({
      description: t(
        'fertWork.deleteConfirmMultiple',
        'Are you sure you want to delete {count} session(s)? This action cannot be undone.',
        {
          count: selectedRows.length,
        },
      ),
      title: t('fertWork.confirmDelete', 'Confirm Delete'),
      confirmVariant: 'destructive',
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          if (onDeleteMultiple) {
            await onDeleteMultiple(selectedRows.map((row) => row.id));
          } else {
            for (const row of selectedRows) {
              await onDelete(row.id);
            }
          }
          setSelectedRows([]);
          gridRef.current?.api.deselectAll();
        } finally {
          setIsDeleting(false);
        }
      },
    });
  }, [selectedRows, onDelete, onDeleteMultiple, confirm, t]);

  const emitFilterChange = useCallback(
    (
      overrides: Partial<{
        siteId: string | null;
        date: string | null;
        status: string | null;
        createdById: string | null;
      }>,
    ) => {
      const merged = {
        siteId:
          overrides.siteId !== undefined ? overrides.siteId : filterSiteId,
        date:
          overrides.date !== undefined
            ? overrides.date
            : filterDate
              ? format(filterDate, 'yyyy-MM-dd')
              : null,
        status:
          overrides.status !== undefined
            ? overrides.status
            : filterStatus !== null
              ? String(filterStatus)
              : null,
        createdById:
          overrides.createdById !== undefined
            ? overrides.createdById
            : (filterTech?.id ?? null),
      };
      onFilterChange?.(merged);
    },
    [filterSiteId, filterDate, filterStatus, filterTech, onFilterChange],
  );

  const rowData = useMemo(() => {
    return sessions.map((session) => ({
      id: session.id,
      sessionId: session.sessionId,
      date: format(new Date(session.createdAt), 'yyyy-MM-dd'),
      aspirationDate: getDateString(session.aspirationDate),
      siteName: session.siteName,
      siteId: session.siteId,
      status: session.status,
      createdBy: session.createdByName,
      runCount: session.runCount,
      sessionType: session.sessionType ?? 0,
    }));
  }, [sessions]);

  const handleClearFilters = () => {
    setFilterDate(null);
    setFilterSiteId(null);
    setFilterStatus(null);
    setFilterTech(null);
    onFilterChange?.({
      siteId: null,
      date: null,
      status: null,
      createdById: null,
    });
  };

  const handleRowDoubleClick = (event: any) => {
    const session = event.data;
    if (onRowDoubleClick) {
      onRowDoubleClick(session.id, session.date, session.siteId);
    } else {
      navigate(
        `/fert-work/session?sessionId=${session.id}&date=${session.date}&siteId=${session.siteId}`,
      );
    }
  };

  const handleOpenSession = (session: SessionHistoryRow) => {
    if (onRowDoubleClick) {
      onRowDoubleClick(session.id, session.date, session.siteId);
    } else {
      navigate(
        `/fert-work/session?sessionId=${session.id}&date=${session.date}&siteId=${session.siteId}`,
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-end gap-1">
          <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-48" />
          <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-48" />
          <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-48" />
          <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-48" />
          <div className="h-10 w-10 animate-pulse rounded bg-gray-200" />
        </div>
        <GridLoading
          label={t(
            'fertWork.loadingSessionHistory',
            'Loading session history...',
          )}
          className="h-[600px]"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div data-tour="history-filters">
        <FilterBar
          onClear={handleClearFilters}
          clearTooltip={t('fertWork.clearFilters', 'Clear Filters')}
          extra={
            !isMobile && hasWritePermission && selectedRows.length > 0 ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="destructive"
                      onClick={handleDeleteSelected}
                      disabled={isDeleting}
                    >
                      <i
                        className={
                          isDeleting ? 'pi pi-spin pi-spinner' : 'pi pi-trash'
                        }
                        data-icon="inline-start"
                      />
                      {t(
                        'fertWork.deleteSelected',
                        'Delete Selected ({count})',
                        {
                          count: selectedRows.length,
                        },
                      )}
                    </Button>
                  }
                />
                <TooltipContent side="top">
                  {t(
                    'fertWork.deleteSelectedTooltip',
                    'Delete all selected sessions',
                  )}
                </TooltipContent>
              </Tooltip>
            ) : undefined
          }
        >
          <Select
            items={(fertilizationSites ?? []).map((site) => ({
              label: site.name,
              value: site.id,
            }))}
            value={filterSiteId}
            onChange={(value) => {
              setFilterSiteId(value);
              emitFilterChange({ siteId: value ?? null });
            }}
          >
            <SelectTrigger className="w-full md:w-48">
              <SelectValue
                placeholder={t('fertWork.filterBySite', 'Filter by site')}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={null}>{t('common.all', 'All')}</SelectItem>
              {(fertilizationSites ?? []).map((site) => (
                <SelectItem key={site.id} value={site.id}>
                  {site.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DatePicker
            value={filterDate ?? undefined}
            onChange={(date) => {
              const val = date ?? null;
              setFilterDate(val);
              emitFilterChange({
                date: val ? format(val, 'yyyy-MM-dd') : null,
              });
            }}
            placeholder={t('fertWork.filterByDate', 'Filter by date')}
            className="h-8 w-full md:w-48"
          />

          <Select
            items={statusOptions}
            value={filterStatus}
            onChange={(value) => {
              setFilterStatus(value);
              emitFilterChange({
                status:
                  value !== null && value !== undefined ? String(value) : null,
              });
            }}
          >
            <SelectTrigger className="w-full md:w-48">
              <SelectValue
                placeholder={t('fertWork.filterByStatus', 'Filter by status')}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={null}>{t('common.all', 'All')}</SelectItem>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="w-full md:w-48">
            <TechniciansDropdown
              value={filterTech}
              onChange={(tech: TechnicianItem | null) => {
                setFilterTech(tech);
                emitFilterChange({ createdById: tech?.id ?? null });
              }}
              placeholder={t('fertWork.filterByTech', 'Filter by tech')}
              paramKey="techId"
            />
          </div>
        </FilterBar>
      </div>

      <div data-tour="history-grid">
        <ResponsiveDataView<SessionHistoryRow>
          rowData={rowData}
          columnDefs={columnDefs}
          storageKey="fert-work-history-grid"
          getRowId={(row) => row.id}
          getContextMenuItems={getContextMenuItems}
          paginationPageSize={100}
          paginationPageSizeSelector={[20, 50, 100, 250, 500, 1000]}
          renderCard={(session) => (
            <SessionHistoryCard
              session={session}
              hasWritePermission={hasWritePermission}
              onOpen={handleOpenSession}
              onDelete={onDelete}
            />
          )}
          emptyMessage={t('fertWork.noSessionsFound', 'No sessions found')}
          onRowDoubleClicked={handleRowDoubleClick}
          onSelectionChanged={handleSelectionChanged}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          context={{ onDelete, hasWritePermission }}
          gridRef={gridRef}
        />
      </div>
    </div>
  );
};
